import type { NestedTag, Tag, TreeTag } from '../../types';
import { assignLevels, createPrevTreeTagMap, sortFn } from './helpers';

export const buildTreeTags = (allTags: Tag[], prevTreeTags: TreeTag[]) => {
  const prevTreeTagMap = createPrevTreeTagMap(prevTreeTags);

  // Step 1: Update tags with properties in preparation for nesting
  const flatNestedTags = allTags.reduce<Record<string, NestedTag>>(
    (acc, tag) => {
      let parentId: string | undefined;

      if (tag.ancestors) {
        [parentId] = tag.ancestors.split('/').slice(-1);
      }

      const prevTag = prevTreeTagMap.get(tag.id);

      const nestedTag: NestedTag = {
        ...tag,
        level: 0,
        open: prevTag ? Boolean(prevTag.open) : false,
        parentId,
        hasChildren: allTags.some(child => child.ancestors?.endsWith(tag.id)),
        children: []
      };

      acc[nestedTag.id] = nestedTag;

      return acc;
    },
    {}
  );

  // Step 2: Build a temporary nested structure
  const nestedTags = Object.values(flatNestedTags).reduce<NestedTag[]>(
    (acc, tag) => {
      // A tag with a parentId is a child tag
      if (tag.parentId && flatNestedTags[tag.parentId]) {
        flatNestedTags[tag.parentId].children.push(tag);
      } else {
        acc.push(tag);
      }
      return acc;
    },
    []
  );

  // Step 3: Traverse the built tree to assign correct levels (mutation)

  // Start assigning levels from the root at level 0
  assignLevels(nestedTags, 0);

  // Step 4: Flatten nestedTags using Depth-First Search (DFS)
  const treeTags: TreeTag[] = [];

  const traverseAndFlatten = (nodes: NestedTag[]) => {
    nodes.sort(sortFn); // Sort siblings

    for (const node of nodes) {
      const { children, ...rest } = node;

      treeTags.push(rest);

      if (children.length > 0) {
        traverseAndFlatten(children);
      }
    }
  };

  traverseAndFlatten(nestedTags);

  return treeTags;
};
